import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import type { InterviewTypeValue } from '@interviewgator/shared';
import { Icon } from '@/components/icon/index.js';
import type { IconName } from '@/components/icon/types.js';
import { GatorMarkCircle } from '../shared/GatorMarkCircle.js';
import { useSimulationStore } from '../../store/simulationStore.js';
import { useLocalizedRole } from '../../lib/roleDisplay.js';
import { cn } from '../../lib/utils.js';

const STEP_INTERVAL_MS = 2600;
const TIP_INTERVAL_MS = 6500;
const CHIP_INTERVAL_MS = 650;

const EXPERIENCE_KEY: Record<string, string> = {
  JUNIOR: 'setup.step1.experience.junior',
  MID: 'setup.step1.experience.mid',
  SENIOR: 'setup.step1.experience.senior',
  LEAD: 'setup.step1.experience.lead',
};

const ORBIT_ANGLES = [0, 90, 180, 270];

/**
 * The centerpiece for interview-question generation — a full-screen, cinematic
 * wait state that turns a slow API call into a moment that feels crafted:
 *
 *  - a drifting accent aurora + faint grid backdrop (design-system tokens only),
 *  - the Gator mark inside a rotating conic sweep, counter-rotating arcs, and
 *    orbiting accent dots ("the AI is thinking"),
 *  - a personalized headline naming the candidate's exact role,
 *  - a narrated step checklist that advances on a timer,
 *  - interview-theme chips that assemble one by one,
 *  - a perceived-progress bar with a live percentage,
 *  - rotating Gator tips in the app's speech-bubble voice.
 *
 * All looping motion is gated behind prefers-reduced-motion.
 */
export function InterviewGeneratingOverlay() {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const loop = !prefersReducedMotion;

  const setupConfig = useSimulationStore((s) => s.setupConfig);
  const localizeRole = useLocalizedRole();

  const roleLabel = localizeRole(setupConfig);
  const experienceLabel = setupConfig.experienceLevel
    ? t(EXPERIENCE_KEY[setupConfig.experienceLevel] ?? '', { defaultValue: '' })
    : '';
  const interviewLabel = setupConfig.interviewType
    ? t(`setup.step2.cards.${setupConfig.interviewType as InterviewTypeValue}.headline`, {
        defaultValue: '',
      })
    : '';

  const headline = t('setup.generating.headline', {
    role: roleLabel || t('setup.generating.roleFallback', { defaultValue: 'personalized' }),
    defaultValue: 'Crafting your {{role}} interview',
  });

  const sublineParts = [interviewLabel, experienceLabel].filter(Boolean);
  const subline = [...sublineParts, t('setup.generating.threeStages', { defaultValue: 'Three stages' })]
    .filter(Boolean)
    .join('  ·  ');

  const steps = useMemo<Array<{ icon: IconName; label: string }>>(
    () => [
      { icon: 'compass', label: t('setup.generating.step1', { defaultValue: 'Studying the role' }) },
      { icon: 'sparkles', label: t('setup.generating.step2', { defaultValue: 'Selecting question themes' }) },
      { icon: 'settings-2', label: t('setup.generating.step3', { defaultValue: 'Calibrating the difficulty' }) },
      { icon: 'file-text', label: t('setup.generating.step4', { defaultValue: 'Writing your questions' }) },
      { icon: 'three-stages', label: t('setup.generating.step5', { defaultValue: 'Setting the three stages' }) },
    ],
    [t],
  );

  const chips = useMemo(
    () => [
      t('setup.generating.chip1', { defaultValue: 'Behavioral' }),
      t('setup.generating.chip2', { defaultValue: 'Technical depth' }),
      t('setup.generating.chip3', { defaultValue: 'Communication' }),
      t('setup.generating.chip4', { defaultValue: 'Role fit' }),
      t('setup.generating.chip5', { defaultValue: 'Curveballs' }),
    ],
    [t],
  );

  const tips = useMemo(
    () => [
      t('generating.tips.t1', {
        defaultValue:
          'Strong answers follow a shape: situation, action, result. Numbers make results memorable.',
      }),
      t('generating.tips.t2', {
        defaultValue: 'Pausing to think before answering reads as confidence, not hesitation.',
      }),
      t('generating.tips.t3', {
        defaultValue:
          'Interviewers remember stories better than lists — one concrete example beats three claims.',
      }),
      t('generating.tips.t4', {
        defaultValue: 'Questions you ask at the end are part of your answer. Prepare two good ones.',
      }),
      t('generating.tips.t5', {
        defaultValue: 'Reviewing your own reports after a day works better than re-reading them immediately.',
      }),
    ],
    [t],
  );

  // Step checklist advances on a timer, parking on the last step. Real
  // completion is signaled by the page unmounting this overlay + navigating.
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setStepIndex((current) => Math.min(current + 1, steps.length - 1));
    }, STEP_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [steps.length]);

  // Theme chips assemble one by one for the "being built" effect.
  const [revealedChips, setRevealedChips] = useState(1);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setRevealedChips((current) => Math.min(current + 1, chips.length));
    }, CHIP_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [chips.length]);

  // Perceived progress: quick start, asymptotic crawl toward 94%.
  const [progress, setProgress] = useState(6);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setProgress((current) => current + Math.max(0.2, (94 - current) * 0.035));
    }, 220);
    return () => window.clearInterval(timer);
  }, []);
  const progressValue = Math.min(Math.round(progress), 94);

  const [tipIndex, setTipIndex] = useState(0);
  const [tipVisible, setTipVisible] = useState(true);
  const tipTimeoutRef = useRef<number>(0);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setTipVisible(false);
      tipTimeoutRef.current = window.setTimeout(() => {
        setTipIndex((current) => (current + 1) % tips.length);
        setTipVisible(true);
      }, 320);
    }, TIP_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(tipTimeoutRef.current);
    };
  }, [tips.length]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={headline}
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-ig-bg-primary"
    >
      {/* Ambient backdrop: drifting accent aurora + faint dot grid. */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <motion.div
          className="absolute -left-[10%] top-[-15%] h-[55vh] w-[55vh] rounded-full bg-ig-accent/[0.10] blur-[120px]"
          animate={loop ? { x: [0, 60, 0], y: [0, 40, 0], opacity: [0.6, 1, 0.6] } : undefined}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-[-20%] right-[-10%] h-[60vh] w-[60vh] rounded-full bg-ig-accent/[0.08] blur-[130px]"
          animate={loop ? { x: [0, -50, 0], y: [0, -30, 0], opacity: [0.5, 0.9, 0.5] } : undefined}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, var(--ig-border-strong) 1px, transparent 0)',
            backgroundSize: '34px 34px',
            maskImage: 'radial-gradient(ellipse 70% 60% at 50% 45%, black 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 45%, black 30%, transparent 75%)',
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-[1040px] px-6 py-8 md:px-10"
      >
        <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-14 lg:gap-16">
          {/* ── Left: brand hero ──────────────────────────────────────── */}
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            {/* Orbital centerpiece */}
            <div className="relative flex h-32 w-32 items-center justify-center md:h-36 md:w-36">
              {/* Breathing halo */}
              <span className="absolute inset-0 rounded-full bg-ig-accent/10 motion-safe:animate-ping motion-safe:[animation-duration:2.6s]" />
              {/* Rotating conic sweep, masked to a thin ring */}
              <motion.span
                className="absolute -inset-1 rounded-full"
                style={{
                  background:
                    'conic-gradient(from 0deg, transparent 0deg, var(--ig-accent) 260deg, transparent 340deg)',
                  maskImage:
                    'radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 2px))',
                  WebkitMaskImage:
                    'radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 2px))',
                }}
                animate={loop ? { rotate: 360 } : undefined}
                transition={{ duration: 3.4, repeat: Infinity, ease: 'linear' }}
              />
              {/* Counter-rotating inner arc for depth */}
              <span className="absolute inset-4 rounded-full border border-transparent border-b-ig-accent/40 motion-safe:animate-spin motion-safe:[animation-duration:2.2s] motion-safe:[animation-direction:reverse]" />
              {/* Orbiting accent dots */}
              <motion.div
                className="absolute inset-0"
                animate={loop ? { rotate: 360 } : undefined}
                transition={{ duration: 7, repeat: Infinity, ease: 'linear' }}
              >
                {ORBIT_ANGLES.map((angle) => (
                  <span
                    key={angle}
                    className="absolute left-1/2 top-1/2 block"
                    style={{ transform: `rotate(${angle}deg) translateY(-72px)` }}
                  >
                    <span className="block h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-ig-accent shadow-[0_0_8px_var(--ig-accent)]" />
                  </span>
                ))}
              </motion.div>
              <motion.div
                animate={loop ? { scale: [1, 1.04, 1] } : undefined}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="relative flex h-[72px] w-[72px] items-center justify-center rounded-full bg-ig-accent-subtle md:h-20 md:w-20"
              >
                <GatorMarkCircle className="h-14 w-14 md:h-16 md:w-16" alt="" />
              </motion.div>
            </div>

            {/* Eyebrow */}
            <p className="mt-7 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-ig-accent">
              {t('setup.generating.eyebrow', { defaultValue: 'Building your simulation' })}
            </p>

            {/* Personalized headline */}
            <h1 className="mt-3 text-balance font-display text-[30px] font-light leading-[1.08] text-ig-text-primary md:text-[42px]">
              {headline}
            </h1>

            {subline ? (
              <p className="mt-3 font-sans text-[14px] text-ig-text-secondary md:text-[15px]">{subline}</p>
            ) : null}

            {/* Interview-theme chips assembling */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2 md:justify-start">
              {chips.map((chip, index) => (
                <motion.span
                  key={chip}
                  initial={{ opacity: 0, y: 6, scale: 0.9 }}
                  animate={
                    index < revealedChips
                      ? { opacity: 1, y: 0, scale: 1 }
                      : { opacity: 0, y: 6, scale: 0.9 }
                  }
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ig-border bg-ig-bg-secondary px-3 py-1 font-sans text-[12px] text-ig-text-secondary"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-ig-accent" />
                  {chip}
                </motion.span>
              ))}
            </div>
          </div>

          {/* ── Right: live workspace console ─────────────────────────── */}
          <div className="w-full rounded-lg border border-ig-border bg-ig-bg-secondary/70 p-6 backdrop-blur-sm md:p-7">
            {/* Console header */}
            <div className="flex items-center justify-between border-b border-ig-border pb-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-ig-text-tertiary">
                {t('setup.generating.workspace', { defaultValue: "Gator's workspace" })}
              </span>
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-ig-accent">
                <span className="h-1.5 w-1.5 rounded-full bg-ig-accent motion-safe:animate-pulse motion-safe:[animation-duration:1.4s]" />
                {t('setup.generating.live', { defaultValue: 'Live' })}
              </span>
            </div>

            {/* Progress */}
            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.14em] text-ig-text-tertiary">
                <span>{t('setup.generating.progressLabel', { defaultValue: 'Generating' })}</span>
                <span className="tabular-nums text-ig-text-secondary">{progressValue}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-ig-bg-tertiary">
                <motion.div
                  className="h-full rounded-full bg-ig-accent"
                  animate={{ width: `${progressValue}%` }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                />
              </div>
            </div>

            {/* Narrated step checklist */}
            <ul className="mt-6 flex flex-col gap-2.5">
              {steps.map((step, index) => {
                const state = index < stepIndex ? 'done' : index === stepIndex ? 'active' : 'todo';
                return (
                  <li
                    key={step.label}
                    className={cn(
                      'flex items-center gap-3 font-sans text-[14px] transition-colors duration-300',
                      state === 'done' && 'text-ig-text-tertiary',
                      state === 'active' && 'text-ig-text-primary',
                      state === 'todo' && 'text-ig-text-tertiary/50',
                    )}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center">
                      {state === 'done' ? (
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ig-accent-subtle motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-300">
                          <Icon name="check" className="h-3.5 w-3.5 text-ig-accent" aria-hidden />
                        </span>
                      ) : state === 'active' ? (
                        <span className="flex h-6 w-6 items-center justify-center rounded-full border border-ig-accent/25">
                          <Icon
                            name={step.icon}
                            className="h-3.5 w-3.5 text-ig-accent motion-safe:animate-pulse motion-safe:[animation-duration:1.6s]"
                            aria-hidden
                          />
                        </span>
                      ) : (
                        <span className="flex h-6 w-6 items-center justify-center">
                          <Icon name={step.icon} className="h-3.5 w-3.5 text-ig-text-tertiary/50" aria-hidden />
                        </span>
                      )}
                    </span>
                    <span>
                      {step.label}
                      {state === 'active' ? '…' : ''}
                    </span>
                  </li>
                );
              })}
            </ul>

            {/* Rotating Gator tip */}
            <div className="mt-6 flex items-start gap-3 border-t border-ig-border pt-5">
              <GatorMarkCircle className="h-8 w-8 shrink-0" alt="" />
              <div className="min-h-[60px] flex-1">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-ig-text-tertiary">
                  {t('generating.tipLabel', { defaultValue: 'While you wait' })}
                </p>
                <p
                  className={cn(
                    'mt-1 font-display text-[13px] italic leading-[1.6] text-ig-text-secondary transition-opacity duration-300',
                    tipVisible ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  {tips[tipIndex]}
                </p>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-8 text-center font-sans text-[12px] text-ig-text-tertiary">
          {t('setup.generating.footnote', {
            defaultValue: 'This takes just a few seconds — hang tight while Gator sets the stage.',
          })}
        </p>
      </motion.div>
    </div>
  );
}
